import SearchIcon from '@mui/icons-material/Search';
import { Tooltip } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { demeterApi, demeterPositionsApi } from '../../../../Apis/Apis';
import { ExchangeType, MurexInstrumentModel } from '../../../../Generated/Raven-Demeter';
import useApi from '../../../Apis/Hooks/useApiHook';
import LinkButton, { LinkButtonType } from '../../../Components/Form/Buttons/LinkButton';
import CheckboxInput from '../../../Components/Form/Inputs/CheckboxInput';
import Dropdown from '../../../Components/Form/Inputs/Dropdown';
import { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import ConfirmModal from '../../../Components/Modals/ConfirmModal/ConfirmModal';
import useNotificationHook from '../../../Components/Notifications/useNotificationHook';
import Table from '../../../Components/Tables/Table/Table';
import { TableHeader } from '../../../Components/Tables/Table/TableDefinitions';
import { ApiListResponse } from '../../../Components/Tables/TableWrapper/ApiListResponse';
import Pagination from '../../../Components/Tables/TableWrapper/Pagination';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import MurexInstrumentMappingModal, { MurexInstrumentMapping } from './MurexInstrumentMappingModal';
import styles from './MurexInstrumentMappingPage.module.scss';
import { getExchangeLabel, getMurexMarketLabel, murexInstrumentColumnDefinitions, MurexInstrumentRow } from './MurexInstrumentMappingPageDefinitions';
import UnmappedFinancialInstrumentsTable from './UnmappedFinancialInstrumentsTable';

interface ListMurexInstrumentsRequest {
    isMapped?: boolean;
    murexMarket?: string;
    murexProduct?: string;
    exchange?: ExchangeType;
    search?: string;
    includeInactive: boolean;
}

const allFilterValue = 'All';
const trackingCategory = 'MurexInstrumentMapping';
const defaultItemsPerPage = 50;

const MurexInstrumentMappingPage: React.FC = () => {
    const [translations] = useLanguage();
    const [displayError, displaySuccess] = useNotificationHook();

    const [listMurexInstrumentsRequest, setListMurexInstrumentsRequest] = useState<ListMurexInstrumentsRequest>({ includeInactive: false });
    const [searchTermInput, setSearchTermInput] = useState<string>('');
    const [rows, setRows] = useState<MurexInstrumentRow[]>([]);
    const [selectedInstrumentKeys, setSelectedInstrumentKeys] = useState<string[]>([]);
    const [itemsPerPage, setItemsPerPage] = useState<number>(defaultItemsPerPage);
    const [skip, setSkip] = useState<number>(0);
    const [showEditMappingModal, setShowEditMappingModal] = useState<boolean>(false);
    const [showClearMappingModal, setShowClearMappingModal] = useState<boolean>(false);

    const [, refreshFilters, filtersResponse] = useApi(() => demeterApi.listMurexInstrumentFilters(listMurexInstrumentsRequest.includeInactive), {
        stopAutoExecute: true,
    });
    const [, , mappingOptionsResponse] = useApi(() => demeterApi.getMurexInstrumentMappingOptions());
    const [isLoading, refreshInstruments, listMurexInstrumentsResponse] = useApi(
        () =>
            demeterApi.listMurexInstruments(
                listMurexInstrumentsRequest.isMapped,
                listMurexInstrumentsRequest.murexMarket,
                listMurexInstrumentsRequest.murexProduct,
                listMurexInstrumentsRequest.exchange,
                listMurexInstrumentsRequest.search,
                listMurexInstrumentsRequest.includeInactive,
            ),
        { stopAutoExecute: true },
    );
    const [isUnmappedInstrumentsLoading, refreshUnmappedInstruments, unmappedInstrumentsResponse] = useApi(() =>
        demeterPositionsApi.listDemeterUnmappedFinancialInstruments(),
    );

    useEffect(() => {
        refreshInstruments();
    }, [listMurexInstrumentsRequest]);

    useEffect(() => {
        refreshFilters();
    }, [listMurexInstrumentsRequest.includeInactive]);

    useEffect(() => {
        setRows(listMurexInstrumentsResponse?.rows ?? []);
        setSelectedInstrumentKeys([]);
        setSkip(0);
    }, [listMurexInstrumentsResponse]);

    const applyUpdatedRows = (updatedRows?: MurexInstrumentModel[] | null) => {
        setRows((currentRows) => currentRows.map((row) => updatedRows?.find((x) => x.murexDerivedInstrumentKey === row.murexDerivedInstrumentKey) ?? row));
        setSelectedInstrumentKeys([]);
    };

    const handleSaveMapping = async (mapping: MurexInstrumentMapping) => {
        setShowEditMappingModal(false);

        try {
            const response = await demeterApi.updateMurexInstrumentMappings({ ...mapping, murexDerivedInstrumentKeys: selectedInstrumentKeys });
            applyUpdatedRows(response.data.rows);
            refreshUnmappedInstruments();
            displaySuccess(translations.murexInstrumentMapping.text.mappingUpdated);
        } catch {
            displayError(translations.murexInstrumentMapping.errors.updateMappingFailed);
        }
    };

    const handleClearMapping = async () => {
        setShowClearMappingModal(false);

        try {
            const response = await demeterApi.deleteMurexInstrumentMappings({ murexDerivedInstrumentKeys: selectedInstrumentKeys });
            applyUpdatedRows(response.data.rows);
            refreshUnmappedInstruments();
            displaySuccess(translations.murexInstrumentMapping.text.mappingCleared);
        } catch {
            displayError(translations.murexInstrumentMapping.errors.clearMappingFailed);
        }
    };

    const handleSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') {
            return;
        }

        setListMurexInstrumentsRequest({ ...listMurexInstrumentsRequest, search: searchTermInput || undefined });
    };

    const handleUnmappedInstrumentKeySelected = (murexDerivedInstrumentKey: string) => {
        setSearchTermInput(murexDerivedInstrumentKey);
        setListMurexInstrumentsRequest({ includeInactive: true, search: murexDerivedInstrumentKey });
    };

    const handleRowSelected = (murexDerivedInstrumentKey: string, isSelected: boolean) => {
        setSelectedInstrumentKeys((currentKeys) => {
            if (!isSelected) {
                return currentKeys.filter((x) => x !== murexDerivedInstrumentKey);
            }

            return [...currentKeys, murexDerivedInstrumentKey];
        });
    };

    const pageRows = useMemo(() => rows.slice(skip, skip + itemsPerPage), [rows, skip, itemsPerPage]);

    const mappingDropdownOptions = useMemo(
        () =>
            [...(mappingOptionsResponse?.rows ?? [])]
                .sort((a, b) => `${a.exchange}-${a.displayName}`.localeCompare(`${b.exchange}-${b.displayName}`))
                .map((option) => ({ value: option, label: formattingService.toDisplayName(option) })),
        [mappingOptionsResponse],
    );

    const selectedRows = useMemo(() => rows.filter((row) => selectedInstrumentKeys.includes(row.murexDerivedInstrumentKey)), [rows, selectedInstrumentKeys]);

    // Preselect the mapping the selected instruments already share, so editing an existing mapping opens
    // on its current value. A mixed selection has no single value to show, so it opens empty.
    const selectedMappingOption = useMemo(() => {
        const [firstRow] = selectedRows;

        if (!firstRow?.commodity) {
            return undefined;
        }

        const isSharedMapping = selectedRows.every(
            (row) => row.exchange === firstRow.exchange && (row.subExchange ?? null) === (firstRow.subExchange ?? null) && row.commodity === firstRow.commodity,
        );

        if (!isSharedMapping) {
            return undefined;
        }

        return mappingDropdownOptions.find(
            (option) =>
                option.value.exchange === firstRow.exchange &&
                (option.value.subExchange ?? null) === (firstRow.subExchange ?? null) &&
                option.value.commodity === firstRow.commodity,
        )?.value;
    }, [selectedRows, mappingDropdownOptions]);

    const selectedCurrency = useMemo(() => {
        const [firstRow] = selectedRows;

        if (!firstRow?.currency) {
            return undefined;
        }

        return selectedRows.every((row) => row.currency === firstRow.currency) ? firstRow.currency : undefined;
    }, [selectedRows]);

    const selectedContractSize = useMemo(() => {
        const [firstRow] = selectedRows;

        if (firstRow?.contractSize === null || firstRow?.contractSize === undefined) {
            return undefined;
        }

        return selectedRows.every((row) => row.contractSize === firstRow.contractSize) ? firstRow.contractSize : undefined;
    }, [selectedRows]);

    const handleAllRowsSelected = (isSelected: boolean) => {
        const pageKeys = pageRows.map((x) => x.murexDerivedInstrumentKey);

        setSelectedInstrumentKeys((currentKeys) => {
            if (!isSelected) {
                return currentKeys.filter((x) => !pageKeys.includes(x));
            }

            return [...currentKeys.filter((x) => !pageKeys.includes(x)), ...pageKeys];
        });
    };

    const areAllRowsSelected = pageRows.length > 0 && pageRows.every((row) => selectedInstrumentKeys.includes(row.murexDerivedInstrumentKey));

    const tableData = useMemo(
        () =>
            pageRows.map((row) => ({
                ...row,
                rowKey: row.murexDerivedInstrumentKey,
                actions: (
                    <div className={styles.murex_instrument_mapping_checkbox_cell}>
                        <CheckboxInput
                            isChecked={selectedInstrumentKeys.includes(row.murexDerivedInstrumentKey)}
                            handleIsChecked={(isSelected) => handleRowSelected(row.murexDerivedInstrumentKey, isSelected)}
                            track={{ eventCategory: trackingCategory, eventAction: 'InstrumentSelectionChanged' }}
                        />
                    </div>
                ),
            })),
        [pageRows, selectedInstrumentKeys],
    );

    const getSelectAllHeaderCell = () => (
        <Tooltip title={translations.murexInstrumentMapping.actions.selectAllOnPage} placement="top">
            <div className={styles.murex_instrument_mapping_checkbox_cell}>
                <CheckboxInput
                    isChecked={areAllRowsSelected}
                    handleIsChecked={handleAllRowsSelected}
                    track={{ eventCategory: trackingCategory, eventAction: 'SelectAllInstrumentsChanged' }}
                />
            </div>
        </Tooltip>
    );

    const tableHeaders = useMemo(
        () =>
            [
                {
                    headerValueGetter: getSelectAllHeaderCell,
                    field: 'actions',
                    elementField: 'actions',
                    dataFormat: 'element',
                    // Fixed while the data columns share the remaining width, so the checkbox never
                    // shrinks with the window.
                    width: 60,
                    minimumWidth: 60,
                    pinned: 'left',
                },
                ...murexInstrumentColumnDefinitions,
            ] as TableHeader<MurexInstrumentRow>[],
        [areAllRowsSelected, pageRows],
    );

    const selectedRowHeaderOptions: TableHeader<MurexInstrumentRow> = {
        handleGetCellClasses: (tableDatum) => {
            if (!selectedInstrumentKeys.includes(tableDatum.murexDerivedInstrumentKey)) {
                return [];
            }

            return [styles.murex_instrument_mapping_selected_row];
        },
    };

    const paginationListResponse: ApiListResponse = { totalCount: rows.length, take: itemsPerPage, skip };

    const mappingStatusOptions: SelectInputOption<boolean | typeof allFilterValue>[] = [
        { value: allFilterValue, label: translations.murexInstrumentMapping.actions.allStatuses },
        { value: true, label: translations.murexInstrumentMapping.status.Mapped },
        { value: false, label: translations.murexInstrumentMapping.status.Unmapped },
    ];

    const toTextFilterOptions = (
        values: string[] | null | undefined,
        allLabel: string,
        getLabel: (value: string) => string = (value) => value,
    ): SelectInputOption<string>[] => [{ value: allFilterValue, label: allLabel }, ...(values ?? []).map((value) => ({ value, label: getLabel(value) }))];

    const exchangeOptions: SelectInputOption<ExchangeType | typeof allFilterValue>[] = [
        { value: allFilterValue, label: translations.murexInstrumentMapping.actions.allExchanges },
        ...(filtersResponse?.exchanges ?? []).map((exchange) => ({
            value: exchange,
            label: getExchangeLabel(exchange),
        })),
    ];

    // The backend only lists exchanges that mappings actually use, so before the first mapping exists
    // this filter has nothing to offer. Keep it visible while a selection is active so it can be cleared.
    const showExchangeFilter = (filtersResponse?.exchanges?.length ?? 0) > 0 || !!listMurexInstrumentsRequest.exchange;

    return (
        <div className={styles.murex_instrument_mapping_container}>
            <div className={styles.murex_instrument_mapping_actions_row}>
                <div className={styles.murex_instrument_mapping_filters}>
                    <div className={styles.murex_instrument_mapping_search_area}>
                        <SearchIcon className={styles.murex_instrument_mapping_search_area_icon} />
                        <input
                            onKeyDown={handleSearch}
                            className={styles.murex_instrument_mapping_search_input}
                            placeholder={translations.actions.search}
                            value={searchTermInput}
                            onChange={(event) => setSearchTermInput(event.target.value)}
                        />
                    </div>
                    <div className={styles.murex_instrument_mapping_dropdown_container}>
                        <Dropdown
                            value={listMurexInstrumentsRequest.isMapped ?? allFilterValue}
                            options={mappingStatusOptions}
                            handleOptionChange={(value: boolean | typeof allFilterValue) =>
                                setListMurexInstrumentsRequest({
                                    ...listMurexInstrumentsRequest,
                                    isMapped: value === allFilterValue ? undefined : value,
                                })
                            }
                            track={{ eventCategory: trackingCategory, eventAction: 'MappingStatusFilterChanged' }}
                        />
                    </div>
                    <div className={styles.murex_instrument_mapping_dropdown_container}>
                        <Dropdown
                            value={listMurexInstrumentsRequest.murexMarket ?? allFilterValue}
                            options={toTextFilterOptions(
                                filtersResponse?.murexMarkets,
                                translations.murexInstrumentMapping.actions.allMarkets,
                                getMurexMarketLabel,
                            )}
                            isSearchable
                            handleOptionChange={(value) =>
                                setListMurexInstrumentsRequest({
                                    ...listMurexInstrumentsRequest,
                                    murexMarket: value === allFilterValue ? undefined : value,
                                })
                            }
                            track={{ eventCategory: trackingCategory, eventAction: 'MurexMarketFilterChanged' }}
                        />
                    </div>
                    <div className={styles.murex_instrument_mapping_dropdown_container}>
                        <Dropdown
                            value={listMurexInstrumentsRequest.murexProduct ?? allFilterValue}
                            options={toTextFilterOptions(filtersResponse?.murexProducts, translations.murexInstrumentMapping.actions.allProducts)}
                            isSearchable
                            handleOptionChange={(value) =>
                                setListMurexInstrumentsRequest({
                                    ...listMurexInstrumentsRequest,
                                    murexProduct: value === allFilterValue ? undefined : value,
                                })
                            }
                            track={{ eventCategory: trackingCategory, eventAction: 'MurexProductFilterChanged' }}
                        />
                    </div>
                    {showExchangeFilter && (
                        <div className={styles.murex_instrument_mapping_dropdown_container}>
                            <Dropdown
                                value={listMurexInstrumentsRequest.exchange ?? allFilterValue}
                                options={exchangeOptions}
                                isSearchable
                                handleOptionChange={(value: ExchangeType | typeof allFilterValue) =>
                                    setListMurexInstrumentsRequest({
                                        ...listMurexInstrumentsRequest,
                                        exchange: value === allFilterValue ? undefined : value,
                                    })
                                }
                                track={{ eventCategory: trackingCategory, eventAction: 'ExchangeFilterChanged' }}
                            />
                        </div>
                    )}
                    <CheckboxInput
                        title={translations.murexInstrumentMapping.actions.includeInactive}
                        isChecked={listMurexInstrumentsRequest.includeInactive}
                        handleIsChecked={(includeInactive) => setListMurexInstrumentsRequest({ ...listMurexInstrumentsRequest, includeInactive })}
                        track={{ eventCategory: trackingCategory, eventAction: 'IncludeInactiveChanged' }}
                    />
                </div>
                <div className={styles.murex_instrument_mapping_buttons_row}>
                    {selectedInstrumentKeys.length > 0 ? (
                        <div className={styles.murex_instrument_mapping_selected_count}>
                            {selectedInstrumentKeys.length} {translations.murexInstrumentMapping.text.selected}
                        </div>
                    ) : (
                        <div className={styles.murex_instrument_mapping_selection_hint}>{translations.murexInstrumentMapping.text.selectInstrumentsHint}</div>
                    )}
                    <LinkButton
                        title={translations.murexInstrumentMapping.actions.clearMapping}
                        type={LinkButtonType.White}
                        disabled={selectedInstrumentKeys.length === 0}
                        onClick={() => setShowClearMappingModal(true)}
                        track={{ eventCategory: trackingCategory, eventAction: 'ClearMappingClicked' }}
                    />
                    <LinkButton
                        title={translations.murexInstrumentMapping.actions.editMapping}
                        type={LinkButtonType.Blue}
                        disabled={selectedInstrumentKeys.length === 0}
                        onClick={() => setShowEditMappingModal(true)}
                        track={{ eventCategory: trackingCategory, eventAction: 'EditMappingClicked' }}
                    />
                </div>
            </div>
            <div className={styles.murex_instrument_mapping_table_container}>
                <Table
                    tableHeaders={tableHeaders}
                    tableData={tableData}
                    tableStyle="alternatingRows"
                    isLoading={isLoading}
                    defaultHeaderOptions={selectedRowHeaderOptions}
                />
                {rows.length > itemsPerPage && (
                    <Pagination
                        apiListResponse={paginationListResponse}
                        handlePagination={(updatedPagination) => setSkip(updatedPagination.skip ?? 0)}
                        handleTakeCountUpdate={(updatedTakeCount) => {
                            setItemsPerPage(updatedTakeCount.take ?? defaultItemsPerPage);
                            setSkip(0);
                        }}
                        track={{ eventCategory: trackingCategory, eventAction: 'PaginationChanged' }}
                    />
                )}
            </div>
            <UnmappedFinancialInstrumentsTable
                unmappedInstruments={unmappedInstrumentsResponse?.rows ?? []}
                isLoading={isUnmappedInstrumentsLoading}
                handleMurexInstrumentKeySelected={handleUnmappedInstrumentKeySelected}
            />
            <MurexInstrumentMappingModal
                showModal={showEditMappingModal}
                mappingOptions={mappingDropdownOptions}
                currentMappingOption={selectedMappingOption}
                currentCurrency={selectedCurrency}
                currentContractSize={selectedContractSize}
                handleSave={handleSaveMapping}
                handleCancel={() => setShowEditMappingModal(false)}
            />
            <ConfirmModal
                header={translations.murexInstrumentMapping.text.clearMappingHeader}
                showModal={showClearMappingModal}
                message={translations.murexInstrumentMapping.text.clearMappingConfirmation}
                handleConfirm={handleClearMapping}
                handleCancel={() => setShowClearMappingModal(false)}
                track={{ eventCategory: trackingCategory, eventAction: 'ClearMapping' }}
            />
        </div>
    );
};

export default MurexInstrumentMappingPage;
